import { QueryClient } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { AuthContextType } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

interface PollForFileStatusParams {
  qc: QueryClient;
  vectorStoreId: string;
  fileId: string;
  auth: AuthContextType | undefined;
  workspaceId: number;
  checkCondition: (fileIds: string[]) => boolean;
  queryKeys: {
    files: readonly unknown[];
    stores: readonly unknown[];
  };
  onConfirmed?: () => void;
  attempt?: number;
  maxAttempts?: number;
  errorMessage?: string;
}

/**
 * Generic polling function to check file status in a vector store
 * Uses exponential backoff and only invalidates queries once condition is met
 */
export const pollForFileStatus = async ({
  qc,
  vectorStoreId,
  fileId,
  auth,
  workspaceId,
  checkCondition,
  queryKeys,
  onConfirmed,
  attempt = 0,
  maxAttempts = 8,
  errorMessage = "Error polling for file status",
}: PollForFileStatusParams): Promise<void> => {
  if (attempt >= maxAttempts) {
    // Fallback: invalidate anyway after max attempts
    qc.invalidateQueries({ queryKey: queryKeys.files });
    qc.invalidateQueries({ queryKey: queryKeys.stores });
    if (onConfirmed) {
      onConfirmed();
    }
    return;
  }

  const delay = Math.min(500 * Math.pow(2, attempt), 8000);
  await new Promise(resolve => setTimeout(resolve, delay)); // eslint-disable-line no-promise-executor-return

  try {
    // Check file status in the vector store
    const fileIdsResponse = await fetch(
      `${API_URL}/api/dashboard/v2/vector-store/${vectorStoreId}/files?workspace_id=${workspaceId}`,
      {
        headers: authHeader(auth!.userToken!),
      },
    ).then(handleResponse);

    const fileIds = fileIdsResponse?.data?.map((f: { id: string }) => f.id) || [];
    const conditionMet = checkCondition(fileIds);

    if (conditionMet) {
      // Condition met, invalidate queries
      qc.invalidateQueries({ queryKey: queryKeys.files });
      qc.invalidateQueries({ queryKey: queryKeys.stores });
      if (onConfirmed) {
        onConfirmed();
      }
      return;
    }

    // Condition not met, continue polling
    return pollForFileStatus({
      qc,
      vectorStoreId,
      fileId,
      auth,
      workspaceId,
      checkCondition,
      queryKeys,
      onConfirmed,
      attempt: attempt + 1,
      maxAttempts,
      errorMessage,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(errorMessage, error);
    // On error, continue polling
    return pollForFileStatus({
      qc,
      vectorStoreId,
      fileId,
      auth,
      workspaceId,
      checkCondition,
      queryKeys,
      onConfirmed,
      attempt: attempt + 1,
      maxAttempts,
      errorMessage,
    });
  }
};
